/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BedIcon from '@mui/icons-material/Bed';
import CategoryIcon from '@mui/icons-material/Category';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { toast } from 'react-toastify';
import { getApi } from 'views/services/api';

export default function StepDone({ hotel, hotelId, onFinish }) {
  const theme = useTheme();
  const [summary, setSummary] = useState(null);
  const [finishing, setFinishing] = useState(false);

  const bookingUrl = hotel?.slug ? `https://app.segocloud.com/reservas/${hotel.slug}` : null;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const [roomsRes, typesRes, staysRes] = await Promise.all([
          getApi(`api/room/viewallrooms/${hotelId}`),
          getApi(`api/room/room-types/list/${hotelId}`),
          getApi(`api/hotel/stay-types/list/${hotelId}`)
        ]);
        setSummary({
          rooms: Array.isArray(roomsRes?.data) ? roomsRes.data.length : 0,
          types: Array.isArray(typesRes?.data) ? typesRes.data.length : 0,
          stays: Array.isArray(staysRes?.data) ? staysRes.data.length : 0
        });
      } catch (e) {
        console.error(e);
        setSummary({ rooms: 0, types: 0, stays: 0 });
      }
    };
    fetchSummary();
  }, [hotelId]);

  const copyLink = () => {
    if (!bookingUrl) return;
    navigator.clipboard
      .writeText(bookingUrl)
      .then(() => toast.success('Link copiado'))
      .catch(() => toast.error('No se pudo copiar'));
  };

  const handleFinish = async () => {
    setFinishing(true);
    await onFinish();
  };

  return (
    <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(theme.palette.success.main, 0.1)
        }}
      >
        <CelebrationIcon sx={{ fontSize: 44, color: theme.palette.success.main }} />
      </Box>

      <Typography variant="h3">¡Tu hotel está listo!</Typography>

      {summary ? (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap justifyContent="center">
          <Chip icon={<CategoryIcon />} label={`${summary.types} tipos de habitación`} color="primary" variant="outlined" />
          <Chip icon={<BedIcon />} label={`${summary.rooms} habitaciones`} color="primary" variant="outlined" />
          <Chip icon={<ScheduleIcon />} label={`${summary.stays} tipos de estancia`} color="primary" variant="outlined" />
        </Stack>
      ) : (
        <CircularProgress size={24} />
      )}

      {bookingUrl && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, maxWidth: 520, width: '100%' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Tu motor de reservas ya está en línea
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Compártelo con tus clientes o ponlo en tus redes: recibirás pre-reservas directas sin comisiones.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Typography
              variant="body2"
              component="a"
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main, wordBreak: 'break-all' }}
            >
              {bookingUrl}
            </Typography>
            <Tooltip title="Copiar link">
              <IconButton size="small" onClick={copyLink}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      <Button
        variant="contained"
        size="large"
        color="success"
        onClick={handleFinish}
        disabled={finishing}
        sx={{ px: 6, py: 1.5, borderRadius: 2, fontWeight: 600 }}
      >
        {finishing ? 'Finalizando...' : 'Ir a mi panel'}
      </Button>
    </Stack>
  );
}
